<script setup lang="ts">
import axios from "axios";
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxInput,
  ComboboxRoot,
  ComboboxViewport,
  ComboboxItem,
  ComboboxEmpty,
  ComboboxPortal,
} from "reka-ui";
import debounce from "lodash.debounce";
import { computed, onMounted, ref, watch } from "vue";

import { config } from "@/config";

import { authMachine } from "@/machines/auth-machine";

import { useWindow } from "@/hooks/use-window";

export interface Props {
  inputId?: string;
  queryParamName?: string;
  limit?: number;
  minChars?: number;
  apiEndpoint?: string;
  delayTime?: number;
  placeholder?: string;
  classes?: string;
  inputClasses?: Record<string, boolean>;
  modelValue?: string;
  highlighting?: (item: string) => void;
  render?: (items: string[]) => string[];
  getResponse?: (response: unknown) => { data: string[]; totalCount: number };
  fetch?: (query: string) => Promise<unknown>;
  objectArray?: string[];
  isItemPresent?: (item: string) => boolean;
  isSearch?: boolean;
}

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "blur", value: string): void;
  (e: "hit", value: string): void;
}>();
const props = withDefaults(defineProps<Props>(), {
  selectFirst: false,
  queryParamName: ":keyword",
  limit: 10,
  minChars: 2,
  delayTime: 500,
  showSearchingFlag: false,
  NoResultText: "No result",
  SearchingText: "Searching...",
  modelValue: "",
  render: (a: string[]) => a,
  canAddEntry: false,
  addEntryText: "Create New",
  isSearch: false,
  getResponse: (response: unknown) => ({
    totalCount: (response as Record<string, unknown>).totalCount as number,
    data: (response as Record<string, unknown>).data as string[],
  }),
});

const window = useWindow();

const query = ref("");
const loading = ref(false);
const hasSearched = ref(false);

const fetchedData = ref<string[]>([]);
const data = computed<string[]>(() => props.objectArray ?? fetchedData.value);
const items = computed<string[]>(() =>
  props.render(
    (props.limit ? data.value.slice(0, props.limit) : data.value).filter((entity) =>
      entity.toLowerCase().includes(query.value.toLowerCase()),
    ),
  ),
);

const isEmpty = computed(() => query.value === "");
const hasItems = computed(() => items.value.length > 0);

function hit(value: string) {
  emit("hit", value);
  query.value = value;

  reset();
}

function reset() {
  fetchedData.value = [];
  loading.value = false;
  hasSearched.value = false;
}

function highlightItem(item: string) {
  if (props.highlighting) {
    props.highlighting(item);
    return;
  }

  const matches = item.match(new RegExp(query.value, "ig"));

  matches?.forEach((match) => {
    item = item.replace(match, `<b>${match}</b>`);
  });

  return item;
}

if (props.objectArray == null) {
  watch(
    query,
    debounce(async (value: string) => {
      emit("update:modelValue", value);

      if (value.length < props.minChars) return;

      let response: unknown;

      try {
        loading.value = true;

        if (props.fetch == null && props.apiEndpoint != null) {
          const token = authMachine.getSnapshot().context.token?.access_token;

          const regex = new RegExp(props.queryParamName, "g");
          response = await axios.get(
            `${config.apiUrl}${props.apiEndpoint.replace(regex, value)}`,
            token == null ? undefined : { headers: { Authorization: `Bearer ${token}` } },
          );
        } else if (props.fetch != null) {
          response = await props.fetch(value);
        }

        if (response == null) return;

        if (value === query.value) {
          fetchedData.value = props.getResponse(response).data;
          hasSearched.value = true;
        }
      } catch (error) {
        console.error(error);
      } finally {
        loading.value = false;
      }
    }, props.delayTime),
  );
} else {
  watch(query, (value) => {
    emit("update:modelValue", value);

    loading.value = false;
    hasSearched.value = value.length >= props.minChars;
  });
}

watch(
  () => props.modelValue,
  (value) => (query.value = query.value !== value ? value : query.value),
);

onMounted(() => {
  query.value = props.modelValue;
});
</script>
<template>
  <ComboboxRoot class="tw-relative" :class="[classes]">
    <ComboboxAnchor>
      <ComboboxInput
        :id="inputId"
        :model-value="query"
        :class="[inputClasses]"
        :placeholder="placeholder"
        type="text"
        autocomplete="off"
        class="type-ahead-select taller tw-form-input"
        @keydown.enter.prevent
        @update:model-value="(value) => hit(value.toString())"
      />
    </ComboboxAnchor>
    <ComboboxPortal :to="window.document.body">
      <ComboboxContent
        avoid-collisions
        position="popper"
        :side-offset="4"
        class="tw-z-[100] tw-max-h-[calc(var(--reka-combobox-content-available-height)-1rem)] tw-w-[--reka-combobox-trigger-width] tw-overflow-hidden tw-rounded tw-border tw-border-gray-500 tw-bg-white tw-shadow-md"
      >
        <ComboboxViewport>
          <ComboboxEmpty class="tw-p-1 tw-py-2 tw-text-center tw-text-xs tw-font-medium tw-text-gray-400">
            {{ loading ? "Loading" : "No options" }}
          </ComboboxEmpty>
          <template v-if="!loading && hasSearched && hasItems && !isEmpty">
            <ComboboxItem
              v-for="item in items"
              :key="item"
              class="tw-flex tw-min-h-7 tw-select-none tw-items-center tw-rounded-sm tw-pl-6 tw-pr-1 hover:tw-cursor-pointer data-[highlighted]:tw-bg-gray-200 data-[highlighted]:tw-text-gray-800 data-[highlighted]:tw-outline-none"
              :value="item"
            >
              <a v-html="highlightItem(item)" />
            </ComboboxItem>
            <div v-if="data.length > 0" class="tw-sticky tw-bottom-0 tw-bg-white tw-p-1">
              <span class="tw-text-gray-600">Showing {{ items.length }} of {{ data.length }}</span>
            </div>
          </template>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>
<style lang="scss" scoped>
ul {
  z-index: 1051;
}
.fa-search {
  top: 10px;
  right: 10px;
  z-index: 10;
}

.small {
  font-size: 10px;
  .fa-search {
    top: 6px;
  }
  .tw-form-input {
    padding-top: 0.125rem;
    padding-bottom: 0.125rem;
  }
}

.tw-max-h-80 {
  max-height: 20rem;
}
</style>
